import { clsx } from 'clsx';
import { Button } from 'src/components/ui/button';
import { SearchInput } from 'src/components/SearchInput/SearchInput';
import {
  DeleteItemsKnowledgeBaseIcon,
  MoveItemIcon,
  MoveItemsKnowledgeBaseIcon,
} from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/components/CurrentKnowledgeBaseIcons/CurrentKnowledgeBaseIcons';
import { AddKnowledgeBaseSourceModal } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/create/AddKnowledgeBaseSourceModal/AddKnowledgeBaseSourceModal';
import { AddKnowledgeBaseFolderModal } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/create/AddKnowledgeBaseFolderModal/AddKnowledgeBaseFolderModal';
import { FiltersKnowledgeBaseModal } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/components/FiltersKnowledgeBaseModal/FiltersKnowledgeBaseModal';
import { DeleteKnowledgeBaseItemsModal } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/delete/DeleteKnowledgeBaseItemsModal/DeleteKnowledgeBaseItemsModal';
import { MoveKnowledgeBaseItemModal } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/components/MoveKnowledgeBaseItemModal/MoveKnowledgeBaseItemModal';
import { SelectAllCheckboxKnowledgeBasePage } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/components/CurrentKnowledgeBaseTableList/SelectAllCheckboxKnowledgeBase';
import { KnowledgeBaseFiltersList } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/components/KnowledgeBaseFiltersList/KnowledgeBaseFiltersList';
import { useKBContext } from 'src/provider/KnowledgeBaseProvider';
import type { useKnowledgeBaseSelection } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/hooks/useKnowledgeBaseSelection';
import type { useKnowledgeBaseFileActions } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/hooks/useKnowledgeBaseFileActions';

// Integration and website branches hold synced content that can't be added to, moved or deleted.
const READ_ONLY_BRANCH_TYPES = new Set(['integration', 'website']);

type CurrentKnowledgeBaseToolbarProps = Pick<
  ReturnType<typeof useKnowledgeBaseSelection>,
  | 'moveContentMode'
  | 'deleteContentMode'
  | 'checkedItemsCount'
  | 'checkedFilesCount'
  | 'handleSelectAllChange'
  | 'cancelCheckboxMode'
  | 'setMoveContentMode'
  | 'setDeleteContentMode'
> &
  Pick<
    ReturnType<typeof useKnowledgeBaseFileActions>,
    | 'handleAddFolderItem'
    | 'addKnowledgeBaseSource'
    | 'onAddKnowledgeBaseSourceOpen'
    | 'handleItemsDelete'
    | 'handleMoveItems'
  > & {
    isActionButtonsDisabled: boolean;
    onFileSearch: (value: string) => void;
  };

export const CurrentKnowledgeBaseToolbar = ({
  moveContentMode,
  deleteContentMode,
  checkedItemsCount,
  checkedFilesCount,
  handleSelectAllChange,
  cancelCheckboxMode,
  setMoveContentMode,
  setDeleteContentMode,
  handleAddFolderItem,
  addKnowledgeBaseSource,
  onAddKnowledgeBaseSourceOpen,
  handleItemsDelete,
  handleMoveItems,
  isActionButtonsDisabled,
  onFileSearch,
}: CurrentKnowledgeBaseToolbarProps) => {
  const { activeBranch } = useKBContext();
  const isReadOnlyBranch = READ_ONLY_BRANCH_TYPES.has(activeBranch?.type);

  return (
    <div className={'flex flex-row justify-between gap-32'}>
      <div className={'flex flex-row gap-2.5'}>
        <AddKnowledgeBaseFolderModal
          handleAddFolderItem={handleAddFolderItem}
          disabled={moveContentMode || deleteContentMode || isReadOnlyBranch}
        />
        <AddKnowledgeBaseSourceModal
          addKnowledgeBaseSource={addKnowledgeBaseSource}
          disabled={moveContentMode || deleteContentMode || isReadOnlyBranch}
          onAddKnowledgeBaseSourceOpen={onAddKnowledgeBaseSourceOpen}
        />
        <FiltersKnowledgeBaseModal />
        <KnowledgeBaseFiltersList />
        {(moveContentMode || deleteContentMode) && (
          <div className={'flex flex-row gap-2.5'}>
            <div
              className={
                'text-xs font-medium text-ebony_opacity_30 flex flex-row gap-2.5 items-center justify-center'
              }
            >
              <div
                className={
                  'px-1.5 py-0.5 bg-ebony text-white rounded-sm-5 min-w-6 flex items-center justify-center'
                }
              >
                {checkedFilesCount}
              </div>
              <div>Selected</div>
            </div>
            <div
              className={
                'text-xs font-medium text-ebony_opacity_30 flex flex-row gap-2.5 items-center justify-center'
              }
            >
              <SelectAllCheckboxKnowledgeBasePage onChange={handleSelectAllChange} />
            </div>
          </div>
        )}
      </div>
      {isActionButtonsDisabled ? (
        <div className={'flex flex-row items-center'}>
          <p className={'text-xs text-center text-ebony_opacity_50 font-medium'}>
            Hold on a moment. We’re updating your Knowledge Base.
            <br /> Bulk actions will be back as soon as the update is done.
          </p>
        </div>
      ) : (
        <div className={'flex flex-1 flex-row gap-2.5 relative justify-end'}>
          <SearchInput
            className={clsx(
              '!min-h-[34px] right-[88px]',
              (moveContentMode || deleteContentMode) && 'hidden',
            )}
            inputClassName={'!max-h-[34px]'}
            onChange={onFileSearch}
            visibleClassName={' w-[calc(100%-100px)]'}
            disabled={isActionButtonsDisabled}
          />
          {moveContentMode && (
            <Button
              variant="outline"
              type="button"
              className="min-w-24 flex flex-fow gap-2 text-ebony_opacity_80 text-base-xs"
              onClick={cancelCheckboxMode}
              disabled={isActionButtonsDisabled}
            >
              <span>Cancel</span>
            </Button>
          )}
          {moveContentMode ? (
            <MoveKnowledgeBaseItemModal
              trigger={
                <Button className="min-w-24 gap-2">
                  Move <MoveItemIcon color={'#FFFFFF'} />
                </Button>
              }
              item={activeBranch}
              handleMoveFiles={handleMoveItems}
              disabled={!checkedItemsCount || isActionButtonsDisabled}
            />
          ) : (
            <Button
              variant="outline"
              type="button"
              aria-label="Select items to move"
              className={clsx('px-1 w-8.5', deleteContentMode && 'hidden')}
              onClick={() => {
                setMoveContentMode(true);
              }}
              disabled={deleteContentMode || isActionButtonsDisabled || isReadOnlyBranch}
            >
              <MoveItemsKnowledgeBaseIcon />
            </Button>
          )}

          {deleteContentMode && (
            <Button
              variant="outline"
              type="button"
              className="min-w-24 flex flex-fow gap-2 text-ebony_opacity_80 text-base-xs"
              onClick={cancelCheckboxMode}
              disabled={isActionButtonsDisabled}
            >
              <span>Cancel</span>
            </Button>
          )}
          {deleteContentMode ? (
            <DeleteKnowledgeBaseItemsModal
              handleItemsDelete={handleItemsDelete}
              disabled={!checkedItemsCount || isActionButtonsDisabled}
            />
          ) : (
            <Button
              variant="outline"
              type="button"
              aria-label="Select items to delete"
              className={clsx('px-2 w-8.5', moveContentMode && 'hidden')}
              onClick={() => {
                setDeleteContentMode(true);
              }}
              disabled={moveContentMode || isActionButtonsDisabled || isReadOnlyBranch}
            >
              <DeleteItemsKnowledgeBaseIcon />
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
